import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import api, { formatApiError } from "@/lib/api";
import { ListingPhoto } from "@/components/PhotoPlaceholder";
import VerifiedExplainer from "@/components/VerifiedExplainer";
import BestOfferBadge from "@/components/BestOfferBadge";
import ListingBadges from "@/components/ListingBadges";
import { formatGBP, conditionLabel, categoryLabel, computeFees, FULFILLMENT_OPTIONS, fulfillmentOption, FEE_TOOLTIP_COPY, PLATFORM_FEE_PERCENT, HOME_DELIVERY_HANDLING_GBP, isClothingListing, clothingSubcategoryLabel, clothingGenderLabel } from "@/lib/marketplace";
import { causeContributionGBP, solicitationStatement } from "@/lib/partnerAgreement";
import { ShieldCheck, ArrowLeft, Sparkles, Loader2, Star, MessageCircle, Truck, MapPin, Users, School, Store, Info, Clock, PiggyBank, Bell, Lock, Send, CheckCircle2, Search, Edit, Package, Pencil, Shield, X, ChevronLeft, ChevronRight, Zap, Hourglass, HeartHandshake, HandCoins } from "lucide-react";
import useVerificationGate from "@/hooks/useVerificationGate";
import ParentVerificationModal from "@/components/ParentVerificationModal";
import { useKidAuth } from "@/context/KidAuthContext";
import { useAuth } from "@/context/AuthContext";
import EditListingModal from "@/components/EditListingModal";
import { subscribeListingUpdates } from "@/lib/listingBus";

const TONE_STYLES = {
  pink: { icon: <Users className="w-5 h-5 text-brandPink" />, bg: "border-brandPink bg-brandPink/5", ring: "border-brandPink" },
  teal: { icon: <School className="w-5 h-5 text-brandTeal" />, bg: "border-brandTeal bg-brandTeal/5", ring: "border-brandTeal" },
  slate: { icon: <Store className="w-5 h-5 text-brandSlate" />, bg: "border-brandSlate bg-brandSlate/5", ring: "border-brandSlate" },
};

const FULFILLMENT_ICONS = {
  parent_handoff: <Users className="w-5 h-5" />,
  school_gate: <School className="w-5 h-5" />,
  local_shop: <Store className="w-5 h-5" />,
  home_delivery: <Truck className="w-5 h-5" />,
};

function InfoTooltip({ label }) {
  const [open, setOpen] = useState(false);
  return (
    <span className="relative inline-flex align-middle">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
        className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-slate-200 text-slate-600 hover:bg-slate-300 transition"
        aria-label="More info about this fee"
        data-testid="fee-info-tooltip-trigger"
      >
        <Info className="w-3 h-3" />
      </button>
      {open && (
        <span
          role="tooltip"
          className="absolute z-30 left-0 top-full mt-2 w-72 bg-brandSlate text-white text-xs font-normal leading-relaxed rounded-xl p-3 shadow-pop normal-case tracking-normal"
          data-testid="fee-info-tooltip-content"
        >
          {label}
        </span>
      )}
    </span>
  );
}

export default function ListingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [listing, setListing] = useState(null);
  const [err, setErr] = useState("");
  const [buying, setBuying] = useState(false);
  const [activePhoto, setActivePhoto] = useState(0);
  const photoCount = listing?.photos?.length || 0;
  useEffect(() => {
    if (photoCount <= 1) return;
    const onKey = (e) => {
      if (e.key === "ArrowLeft") setActivePhoto((i) => (i - 1 + photoCount) % photoCount);
      else if (e.key === "ArrowRight") setActivePhoto((i) => (i + 1) % photoCount);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [photoCount]);
  const [prompts, setPrompts] = useState([]);
  const [showPrompts, setShowPrompts] = useState(false);
  const [sendingPrompt, setSendingPrompt] = useState("");
  const [promptToast, setPromptToast] = useState("");
  // Iteration 131 — Notify Waitlist modal state.
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [notifyEmail, setNotifyEmail] = useState("");
  const [notifyBusy, setNotifyBusy] = useState(false);
  const [notifyErr, setNotifyErr] = useState("");
  const [notifyOk, setNotifyOk] = useState(false);
  // Iteration 118 — free-text composer + realtime contact-mask preview.
  const [freeText, setFreeText] = useState("");
  const [maskWarning, setMaskWarning] = useState("");
  // Iteration 286 — off-platform cash detector for cause-linked items.
  const [cashWarn, setCashWarn] = useState(false);
  const [cashWarnAcked, setCashWarnAcked] = useState(false);
  // Iteration 267 — platform default is Royal Mail Post (home_delivery)
  // for ALL buyers incl. logged-in kids. Kids/guests can freely toggle
  // to Parent Handoff / School Collection; the iteration-262 snap effect
  // only moves the selection OFF a tile that is disabled for this
  // listing — it never forces a choice.
  const [fulfillment, setFulfillment] = useState("home_delivery");
  // Delivery address (only used when fulfillment === "home_delivery")
  // Iteration 214 — recipient name is no longer a separate address
  // field. The Contact Details Full Name is used both for the order
  // contact and the delivery recipient label.
  const [address, setAddress] = useState({ line1: "", line2: "", city: "", postcode: "" });
  // Postcode autocomplete (getAddress.io via /api/address/lookup/{postcode})
  const [lookupPostcode, setLookupPostcode] = useState("");
  const [lookupBusy, setLookupBusy] = useState(false);
  const [lookupErr, setLookupErr] = useState("");
  const [manualEntry, setManualEntry] = useState(false);
  const [selectedAddressLabel, setSelectedAddressLabel] = useState("");
  // Guest buyer contact details — required when picking Parent Handoff
  // or School Gate so the seller's parent can arrange the meet-up.
  const [guestName, setGuestName] = useState("");
  const [guestEmail, setGuestEmail] = useState("");
  const [guestMobile, setGuestMobile] = useState("");
  const { gate, modalProps } = useVerificationGate();

  // Kid session — if present, we swap the Stripe checkout for a Piggy Bank
  // flow (Phase 2 stub: UI ready, backend contract pending — see
  // /app/memory/PHASE2_CONTRACT.md).
  const { kid: kidSession } = useKidAuth();
  const isKid = !!(kidSession && kidSession.kid_id);
  const [kidWallet, setKidWallet] = useState(null);
  // Iteration 261 — this child's own Instant Buy (auto-approve) rule.
  const [instantRule, setInstantRule] = useState(null);
  const [kidPurchasing, setKidPurchasing] = useState(false);
  // Iteration 297 — kid-to-kid Best Offer state (floor auto-accept).
  const [offerPence, setOfferPence] = useState(0);
  const [offerBusy, setOfferBusy] = useState(false);
  const [offerInfo, setOfferInfo] = useState(null);
  const [kidPurchaseFlash, setKidPurchaseFlash] = useState("");
  const [kidPurchaseErr, setKidPurchaseErr] = useState("");
  const [waitlist, setWaitlist] = useState({ joined: false, position: null, count: 0 });
  const [waitlistBusy, setWaitlistBusy] = useState(false);
  const [allowanceRequested, setAllowanceRequested] = useState(false);
  const { user } = useAuth();
  const [editingOpen, setEditingOpen] = useState(false);
  const isOwner = !!(user && listing && listing.owner_id && user.id === listing.owner_id);

  useEffect(() => {
    if (!isKid) return;
    api.get("/kid/wallet").then(({ data }) => setKidWallet(data)).catch(() => setKidWallet(null));
    // Iteration 261 — this child's own Instant Buy rule (read-only).
    api.get("/kid/auto-approve").then(({ data }) => setInstantRule(data)).catch(() => setInstantRule(null));
  }, [isKid]);

  // Iteration 266 — logged-in parent buyers: pre-fill contact details
  // from their profile (name/email, mobile when stored) so handover
  // checkout is one tap. Kids and guests are untouched.
  useEffect(() => {
    if (!user || isKid) return;
    setGuestName((v) => v || user.name || "");
    setGuestEmail((v) => v || user.email || "");
    setGuestMobile((v) => v || user.mobile || user.phone || "");
  }, [user, isKid]);

  useEffect(() => {
    if (!isKid || !id) return;
    api.get(`/kid/waitlist/${id}/status`).then(({ data }) => {
      setWaitlist({ joined: !!data?.joined, position: data?.position ?? null, count: data?.count ?? 0 });
    }).catch(() => { /* silent — treat as not joined */ });
  }, [isKid, id]);

  const kidPurchase = async (amountPenceOverride) => {
    setKidPurchaseErr(""); setKidPurchaseFlash("");
    setKidPurchasing(true);
    try {
      // Iteration 297 — optional pence override lets a below-floor Best
      // Offer route through the standard parent-approval checkout.
      const basePence = Number.isFinite(amountPenceOverride)
        ? amountPenceOverride
        : Math.round((listing.price || 0) * 100);
      const feePence = Math.round(basePence * 0.10);
      const totalPence = basePence + feePence;
      const { data } = await api.post("/orders/kid-purchase", {
        listing_id: id,
        amount_pennies: totalPence,
        fee_pennies: feePence,
        // Iteration 267 — record the buyer's ACTUAL tile selection so the
        // order carries the true fulfilment method downstream.
        fulfillment_method: fulfillment,
      });
      // Iteration 200 — unified post-purchase flow. Internal
      // Kid-Piggy-Bank checkouts now land on the exact same Thank You
      // page as Stripe checkouts, with a `pending=1` flag so the copy
      // reads "Awaiting parent approval" until the parent taps approve.
      const approvalId = data?.approval_id || data?.id || data?.order_id || "";
      const qs = new URLSearchParams();
      if (approvalId) qs.set("order_id", approvalId);
      qs.set("kind", "piggy");
      // Iteration 260 — auto-approved purchases skip the pending state.
      if (data?.auto_approved) {
        qs.set("approved", "1");
        setKidPurchaseFlash("Purchase complete — your parent has been notified!");
      } else {
        qs.set("pending", "1");
        setKidPurchaseFlash("Sent to your parent for approval! You have 24 hours before it expires.");
      }
      navigate(`/order-confirmation?${qs.toString()}`);
    } catch (e) {
      // Phase 2 stub — upstream endpoint isn't live yet. We show a friendly
      // "coming soon" message so parents/kids see the flow but don't get a
      // crash. Once mobile team ships /api/orders/kid-purchase this branch
      // resolves normally.
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "object" ? detail?.message : detail;
      setKidPurchaseErr(msg || "Piggy Bank checkout is arriving next release — hang tight!");
    } finally { setKidPurchasing(false); }
  };

  // Iteration 297 — kid-to-kid Best Offer: floor auto-accept + instant
  // Piggy Bank settlement (no parent wait when the floor is met and the
  // wallet covers it).
  const kidOffer = async () => {
    setKidPurchaseErr(""); setKidPurchaseFlash(""); setOfferInfo(null);
    if (!(offerPence >= 50)) {
      setKidPurchaseErr("Offers start at 50p.");
      return;
    }
    setOfferBusy(true);
    try {
      const { data } = await api.post("/kid/best-offer", {
        listing_id: id,
        offer_pennies: offerPence,
        fulfillment_method: fulfillment,
      });
      if (data?.settled) {
        const qs = new URLSearchParams();
        if (data?.approval_id) qs.set("order_id", data.approval_id);
        qs.set("kind", "piggy");
        qs.set("approved", "1");
        navigate(`/order-confirmation?${qs.toString()}`);
        return;
      }
      if (data?.reason === "below_floor") {
        setOfferInfo({ type: "below_floor" });
      } else if (data?.reason === "insufficient_funds") {
        setOfferInfo({
          type: "insufficient_funds",
          shortfall_pennies: data.shortfall_pennies,
          needed_pennies: data.needed_pennies,
        });
      } else if (data?.accepted) {
        // Accepted but routed to the parent approval queue.
        const qs = new URLSearchParams();
        if (data?.approval_id) qs.set("order_id", data.approval_id);
        qs.set("kind", "piggy");
        qs.set("pending", "1");
        navigate(`/order-confirmation?${qs.toString()}`);
        return;
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "object" ? detail?.message : detail;
      setKidPurchaseErr(msg || "Couldn't place that offer — please try again.");
    } finally { setOfferBusy(false); }
  };

  const joinWaitlist = async () => {
    setWaitlistBusy(true);
    try {
      const { data } = await api.post(`/listings/${id}/waitlist/join`);
      setWaitlist({ joined: true, position: data?.position ?? null, count: data?.count ?? 0 });
    } catch (e) {
      setKidPurchaseErr(formatApiError(e) || "Couldn't join the waitlist. Please try again.");
    } finally { setWaitlistBusy(false); }
  };

  const askAllowance = async (amountPenceOverride) => {
    try {
      await api.post(`/kid/allowance-request`, {
        listing_id: id,
        // Iteration 297 — top-up requests can target a Best Offer amount.
        amount_pennies: Number.isFinite(amountPenceOverride)
          ? amountPenceOverride
          : Math.round((listing.price || 0) * 100),
      });
      setAllowanceRequested(true);
      setKidPurchaseFlash("Asked your parent — they'll see it on their dashboard soon!");
      setTimeout(() => setKidPurchaseFlash(""), 6000);
    } catch (_e) {
      // Stub-friendly: keep the "Asked!" UX even if endpoint 404s so the
      // demo flow still lands nicely. Real endpoint is in the Phase 2 spec.
      setAllowanceRequested(true);
      setKidPurchaseFlash("Asked your parent — they'll see it on their dashboard soon!");
      setTimeout(() => setKidPurchaseFlash(""), 6000);
    }
  };

  const UK_POSTCODE_RE = /^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/i;
  const isHomeDelivery = fulfillment === "home_delivery";
  const isHandoffOrSchool = fulfillment === "parent_handoff" || fulfillment === "school_gate";
  // Iteration 198 — every guest checkout (handoff, school-gate, AND
  // Royal Mail postal) needs name/email/mobile so the seller can
  // reach the buyer for delivery clarifications or handover coord.
  const needsGuestContact = isHandoffOrSchool || isHomeDelivery;
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const guestContactValid =
    !needsGuestContact ||
    (guestName.trim().length >= 2 && EMAIL_RE.test(guestEmail.trim()) && guestMobile.replace(/\D/g, "").length >= 7);

  // Iteration 262 — the mobile app can store legacy choice values like
  // "collection_only" in parent_fulfillment_choices. Expand them to the
  // local handover options so the picker never freezes with EVERY tile
  // disabled for guests/buyers (collection-only = handover/collection
  // methods allowed, Royal Mail excluded). Anti-freeze: a choices list
  // that maps to NO known web option is treated as unrestricted.
  const effectiveParentChoices = useMemo(() => {
    const raw = Array.isArray(listing?.parent_fulfillment_choices)
      ? listing.parent_fulfillment_choices : [];
    if (!raw.length) return null;
    const set = new Set(raw);
    if (set.has("collection_only") || set.has("collection") || set.has("pickup_only")) {
      set.add("parent_handoff");
      set.add("school_gate");
      // Iteration 263 — local_shop intentionally NOT expanded: the tile
      // is permanently disabled platform-wide (Coming Soon).
    }
    const known = new Set(FULFILLMENT_OPTIONS.map((o) => o.value));
    const filtered = new Set([...set].filter((v) => known.has(v)));
    // Iteration 263 — drop permanently-disabled modes (local_shop). A
    // restriction that leaves nothing selectable is treated as
    // unrestricted so the picker can never freeze.
    const selectable = new Set([...filtered].filter((v) => {
      const opt = FULFILLMENT_OPTIONS.find((o) => o.value === v);
      return opt && !opt.disabled;
    }));
    return selectable.size ? selectable : null;
  }, [listing]);

  // Iteration 128 — Royal Mail fallback detector. Recompute per-render
  // so the same rules the option grid uses drive the fallback decision.
  const sellerHasNoDeliveryOptions = useMemo(() => {
    if (!listing) return false;
    // If shipping isn't enabled and no explicit parent-allow list exists,
    // walk the option grid the same way the render loop does and see if
    // ANY buyer-selectable chip is enabled. If none are, Royal Mail
    // becomes the platform-recommended fallback.
    if (listing.shipping_available) return false;
    const anyEnabled = FULFILLMENT_OPTIONS.some((opt) => {
      if (opt.bulkyOnly && listing.package_size !== "bulky") return false;
      if (opt.disabled) return false;
      if (opt.value === "home_delivery") return false; // ignore Royal Mail — that's what we're gating on
      if (effectiveParentChoices && !effectiveParentChoices.has(opt.value)) return false;
      return true;
    });
    return !anyEnabled;
  }, [listing, effectiveParentChoices]);
  const royalMailFallbackActive = sellerHasNoDeliveryOptions;

  // Iteration 262 — never leave the buyer stuck on a DISABLED fulfilment
  // tile (e.g. the Royal Mail default on a collection-only listing, or
  // the parent_handoff default when the fallback kicks in): snap to the
  // first selectable option so totals + the submit button re-evaluate
  // immediately. Generalises the iteration-128 Royal Mail snap.
  useEffect(() => {
    if (!listing) return;
    const optionEnabled = (value) => {
      const opt = FULFILLMENT_OPTIONS.find((o) => o.value === value);
      if (!opt || opt.disabled) return false;
      if (opt.bulkyOnly && listing.package_size !== "bulky") return false;
      if (value === "home_delivery" && !listing.shipping_available && !royalMailFallbackActive) return false;
      if (effectiveParentChoices && !effectiveParentChoices.has(value)
          && !(value === "home_delivery" && royalMailFallbackActive)) return false;
      return true;
    };
    if (!optionEnabled(fulfillment)) {
      const first = FULFILLMENT_OPTIONS.find((o) => optionEnabled(o.value));
      if (first) setFulfillment(first.value);
    }
  }, [listing, effectiveParentChoices, royalMailFallbackActive]);  // eslint-disable-line react-hooks/exhaustive-deps
  const addressValid =
    !isHomeDelivery ||
    (address.line1.trim().length >= 3 &&
      address.city.trim().length >= 2 &&
      UK_POSTCODE_RE.test(address.postcode.trim()));

  const runPostcodeLookup = async () => {
    const pc = lookupPostcode.trim().toUpperCase();
    setLookupErr(""); setSelectedAddressLabel("");
    if (!UK_POSTCODE_RE.test(pc)) {
      setLookupErr("Enter a valid UK postcode (e.g. SW1A 1AA).");
      return;
    }
    setLookupBusy(true);
    // Pre-fill the manual postcode field with what the parent typed so
    // every branch leaves the field populated even before the fetch resolves.
    setAddress((prev) => ({ ...prev, postcode: pc }));
    try {
      const { data } = await api.get(`/address/lookup/${encodeURIComponent(pc)}`);
      if (!Array.isArray(data) || data.length === 0) {
        setLookupErr("Postcode not found — you can still enter it manually below.");
        setManualEntry(true);
      } else {
        // postcodes.io returns one postcode-level row. Auto-fill Town/City +
        // Postcode, reveal the manual fields, and focus Street Line 1 so the
        // parent can immediately type their house number / street.
        const row = data[0];
        setAddress((prev) => ({
          ...prev,
          city: row.town_or_city || prev.city,
          postcode: (row.postcode || pc).toUpperCase(),
        }));
        const parts = Array.isArray(row.formatted_address) && row.formatted_address.length
          ? row.formatted_address
          : [row.town_or_city, row.county, row.postcode].filter(Boolean);
        setSelectedAddressLabel(parts.filter(Boolean).join(", "));
        setManualEntry(true);
        // Focus the street line 1 field once the manual fieldset has rendered.
        setTimeout(() => {
          const el = document.querySelector('[data-testid="delivery-address-line1"]');
          if (el && typeof el.focus === "function") el.focus();
        }, 60);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = (typeof detail === "object" && detail?.message)
        ? detail.message
        : (formatApiError(e) || "Address lookup failed.");
      setLookupErr(msg);
      // Reveal manual fields so the parent can proceed without waiting for
      // the provider to recover.
      setManualEntry(true);
    } finally {
      setLookupBusy(false);
    }
  };

  useEffect(() => {
    api.get(`/listings/${id}`).then(({ data }) => setListing(data)).catch((e) => setErr(formatApiError(e)));
    api.get(`/prompts/templates`).then(({ data }) => setPrompts(data || [])).catch(() => setPrompts([]));
  }, [id]);

  // Iteration 215 — auto-release the reservation lock when the buyer
  // bounces back from Stripe's Cancel button. Without this the toy
  // stays locked (up to 30 min) and the buyer sees "Toy not available"
  // when they try again. Fires exactly once per landing and quietly
  // refreshes the listing so it flips back to `live`.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    if (url.searchParams.get("checkout") !== "cancelled") return;
    const qsSession = url.searchParams.get("session_id");
    const cachedSession = (() => {
      try { return sessionStorage.getItem(`last_checkout_session_id:${id}`); }
      catch { return null; }
    })();
    const sessionIdForCancel = qsSession || cachedSession || null;
    const cachedEmail = (() => {
      try { return sessionStorage.getItem("last_guest_email") || null; }
      catch { return null; }
    })();
    api.post("/checkout/cancel", {
      session_id: sessionIdForCancel,
      listing_id: id,
      buyer_parent_email: cachedEmail,
    }).catch((e) => {
      // Iteration 221 — the cancel route refuses (409) when the purchase
      // already completed or the listing sold; surface the reason to the
      // buyer instead of staying silent. Other failures stay silent.
      if (e?.response?.status === 409) setErr(formatApiError(e));
    }).finally(() => {
      // Clean up the query string so a refresh doesn't re-fire cancel.
      url.searchParams.delete("checkout");
      url.searchParams.delete("session_id");
      const clean = url.pathname + (url.search ? url.search : "") + url.hash;
      try { window.history.replaceState(null, "", clean); } catch { /* noop */ }
      try { sessionStorage.removeItem(`last_checkout_session_id:${id}`); } catch { /* noop */ }
      // Refresh the listing so it flips back to `live`.
      api.get(`/listings/${id}`).then(({ data }) => setListing(data)).catch(() => { /* noop */ });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Any edit anywhere (this modal, another tab) → splice fresh payload.
  useEffect(() => {
    return subscribeListingUpdates((updated) => {
      if (updated && updated.id === id) setListing((prev) => ({ ...(prev || {}), ...updated }));
    });
  }, [id]);

  const buy = async () => {
    if (isHomeDelivery && !addressValid) {
      setErr("Please enter a recipient name and a valid UK delivery address before checkout.");
      return;
    }
    if (needsGuestContact && !guestContactValid) {
      setErr("Please enter your full name, email, and mobile number so the seller can reach you.");
      return;
    }
    // Iteration 147 — Family Safety Check gate removed from guest
    // checkout per directive. Verification remains active for initial
    // parent account signup (Auth flow) but never re-prompts on
    // public listing checkout, so "Buy with Stripe" routes straight
    // to Stripe.
    setBuying(true); setErr("");
    try {
      const body = {
        listing_id: id,
        origin_url: window.location.origin,
        fulfillment_method: fulfillment,
      };
      if (isHomeDelivery) {
        // Iteration 214 — the Contact Details Full Name doubles as
        // the parcel recipient label; buyers no longer type their
        // name twice. `guestName` is required for every guest home-
        // delivery order (validated in `guestContactValid`) so no
        // fallback is needed.
        body.recipient_name = guestName.trim();
        body.delivery_address = {
          line1: address.line1.trim(),
          line2: address.line2.trim() || undefined,
          city: address.city.trim(),
          postcode: address.postcode.trim().toUpperCase(),
          country: "GB",
        };
      }
      if (needsGuestContact) {
        body.buyer_name = guestName.trim();
        body.buyer_parent_email = guestEmail.trim();
        body.buyer_parent_mobile = guestMobile.trim();
      }
      const { data } = await api.post("/checkout/session", body);
      // Iteration 215 — cache the session_id + email so if the buyer
      // hits Stripe's Cancel button the ListingDetail landing can
      // release the reservation without hunting for it.
      try {
        if (data && data.session_id) {
          sessionStorage.setItem(`last_checkout_session_id:${id}`, data.session_id);
        }
        if (needsGuestContact && guestEmail.trim()) {
          sessionStorage.setItem("last_guest_email", guestEmail.trim());
        }
      } catch { /* private browsing quotas — ignore */ }
      window.location.href = data.url;
    } catch (ex) { setErr(formatApiError(ex)); setBuying(false); }
  };

  const sendPrompt = async (promptKey) => {
    setSendingPrompt(promptKey); setPromptToast("");
    try {
      await api.post("/prompts/send", { toy_id: id, prompt_key: promptKey, sender_kid_name: "Buyer" });
      setPromptToast("Message sent to the seller's parent.");
    } catch (e) { setPromptToast(formatApiError(e)); }
    finally { setSendingPrompt(""); }
  };

  // Iteration 118 — debounced client-side mask preview so the "For
  // safety, contact details … are hidden" banner surfaces as the
  // buyer types. Backend also masks on submit so this is UX-only.
  const onFreeTextChange = (v) => {
    setFreeText(v);
    setCashWarn(false); setCashWarnAcked(false);
    if (!v || v.trim().length < 3) { setMaskWarning(""); return; }
    if (onFreeTextChange._t) clearTimeout(onFreeTextChange._t);
    onFreeTextChange._t = setTimeout(async () => {
      try {
        const { data } = await api.post("/messages/preview", { body: v });
        if (data?.was_masked && Array.isArray(data.flags) && data.flags.length) {
          const map = { phone: "phone number", email: "email", postcode: "postcode",
                        address: "street address", url: "external link", social: "social handle" };
          setMaskWarning(data.flags.map((f) => map[f] || f).join(", "));
        } else {
          setMaskWarning("");
        }
      } catch { /* silent — backend is source of truth on submit */ }
    }, 350);
  };

  const sendFreeTextPrompt = async () => {
    if (freeText.trim().length < 3) return;
    // Iteration 286 — off-platform cash payment attempts on cause-linked
    // items surface a compliance notice before the message can send.
    const causeLinked = !!(listing?.cause_kid_id || listing?.seller_account_type === "organization");
    const CASH_RE = /\b(cash|paypal|venmo|revolut|bank transfer|pay outside|cash on collection|cash on pickup)\b/i;
    if (causeLinked && CASH_RE.test(freeText) && !cashWarnAcked) {
      setCashWarn(true);
      setCashWarnAcked(true); // next tap on Send confirms + delivers
      return;
    }
    setSendingPrompt("__free__"); setPromptToast("");
    try {
      // "any_prompt" key acts as a marker so upstream knows this is a
      // buyer-authored freeform question; the actual text lives in `body`.
      await api.post("/prompts/send", {
        toy_id: id,
        prompt_key: "custom_freeform",
        sender_kid_name: "Buyer",
        body: freeText.trim(),
      });
      setPromptToast("Message sent to the seller's parent.");
      setFreeText(""); setMaskWarning(""); setCashWarn(false); setCashWarnAcked(false);
    } catch (e) { setPromptToast(formatApiError(e)); }
    finally { setSendingPrompt(""); }
  };

  if (!listing && !err) return <div className="min-h-screen bg-[#FFFDF7]"><Navbar /><div className="p-20 text-center text-slate-500">Loading…</div></div>;
  if (err && !listing) return <div className="min-h-screen bg-[#FFFDF7]"><Navbar /><div className="p-20 text-center text-rose-600" data-testid="listing-error">{err}</div></div>;

  const photos = listing.photos?.length ? listing.photos : [null];

  return (
    <div className="min-h-screen bg-[#FFFDF7]">
      <Navbar />
      <section className="max-w-6xl mx-auto px-6 lg:px-8 py-10">
        <Link to="/browse" className="inline-flex items-center gap-1 text-sm font-semibold text-slate-600 hover:text-blue-600" data-testid="back-to-browse">
          <ArrowLeft className="w-4 h-4" /> Back to marketplace
        </Link>
        <div className="grid lg:grid-cols-2 gap-10 mt-6">
          <div>
            <div className="card-base overflow-hidden relative group">
              <ListingPhoto src={photos[activePhoto]} alt={listing.title} className="w-full h-[500px] object-cover" testId="listing-photo" />
              {photos.length > 1 && (
                <>
                  <button
                    type="button"
                    aria-label="Previous photo"
                    data-testid="listing-photo-prev"
                    onClick={() => setActivePhoto((i) => (i - 1 + photos.length) % photos.length)}
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/85 hover:bg-white shadow-md flex items-center justify-center text-slate-800 backdrop-blur transition opacity-0 group-hover:opacity-100 focus:opacity-100"
                  >
                    <ChevronLeft className="w-6 h-6" />
                  </button>
                  <button
                    type="button"
                    aria-label="Next photo"
                    data-testid="listing-photo-next"
                    onClick={() => setActivePhoto((i) => (i + 1) % photos.length)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/85 hover:bg-white shadow-md flex items-center justify-center text-slate-800 backdrop-blur transition opacity-0 group-hover:opacity-100 focus:opacity-100"
                  >
                    <ChevronRight className="w-6 h-6" />
                  </button>
                  <div
                    className="absolute bottom-3 right-3 px-2.5 py-1 rounded-full bg-black/55 text-white text-xs font-semibold tracking-wide"
                    data-testid="listing-photo-counter"
                  >
                    {activePhoto + 1} / {photos.length}
                  </div>
                </>
              )}
            </div>
            {photos.length > 1 && (
              <div className="flex gap-2 mt-3" data-testid="listing-thumbs">
                {photos.map((p, i) => (
                  <button key={i} onClick={() => setActivePhoto(i)}
                    className={`w-20 h-20 rounded-xl overflow-hidden border-2 ${i === activePhoto ? "border-blue-500" : "border-transparent"}`}>
                    <img src={p} className="w-full h-full object-cover" alt="" />
                  </button>
                ))}
              </div>
            )}
          </div>
          <div>
            {/* Iteration 84 — explicit two-row badge stack. Row 1 = core
                status (LIVE / category / condition / Verified). Row 2 =
                custom parent-added tags (Bulky / fulfillment / Posted by
                Parent). Both rows render only badges applicable to the
                current listing so a clean listing shows just Row 1. */}
            <div className="flex items-center gap-2 mb-2 flex-wrap" data-testid="listing-detail-badges-row-1">
              <ListingBadges
                listing={listing}
                only={["status", "category", "condition", "clothing", "royal_mail", "cause", "fundraise"]}
              />
              <span className="pill bg-emerald-100 text-emerald-800" data-testid="listing-detail-verified-pill" title="Adult Verified Parent"><ShieldCheck className="w-3 h-3 mr-1" /> Adult Verified <VerifiedExplainer testId="adult-verified-explainer-header" /></span>
              {listing.is_premium && <span className="pill bg-amber-400 text-amber-950"><Star className="w-3 h-3 mr-1 fill-amber-900" /> Featured</span>}
              {isOwner && (
                <button
                  type="button"
                  onClick={() => setEditingOpen(true)}
                  className="pill bg-slate-100 text-slate-800 hover:bg-slate-200 border border-slate-200 transition"
                  data-testid="listing-detail-edit-btn"
                >
                  <Pencil className="w-3 h-3 mr-1" /> Edit listing
                </button>
              )}
            </div>
            <ListingBadges
              listing={listing}
              only={["parcel", "parent_posted"]}
              className="mb-3"
              testid="listing-detail-badges-row-2"
            />
            <h1 className="font-display text-4xl md:text-5xl font-semibold text-slate-900 leading-tight" data-testid="listing-title">{listing.title}</h1>
            <div className="mt-4 flex items-center gap-3 flex-wrap">
              <div className="text-4xl font-display font-semibold text-blue-600" data-testid="listing-price">
                {formatGBP(listing.price)}
                {listing.pay_what_you_can && <span className="text-base font-normal text-slate-500 ml-2">suggested</span>}
              </div>
              {listing.pay_what_you_can && (
                <BestOfferBadge size="md" testId="listing-detail-best-offer-badge" />
              )}
            </div>
            <p className="text-slate-700 mt-6 leading-relaxed whitespace-pre-line" data-testid="listing-description">{listing.description}</p>

            {/* Iteration 274 — clothing attribute card (Size · Gender ·
                Subcategory · Brand) for Clothing listings. */}
            {isClothingListing(listing) && (listing.clothing_subcategory || listing.clothing_size || listing.clothing_gender || listing.clothing_brand) && (
              <div className="card-base p-4 mt-6 border border-rose-200 bg-rose-50/40" data-testid="listing-clothing-attrs">
                <div className="text-xs font-bold uppercase tracking-wider text-rose-700 mb-2">Clothing details</div>
                <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                  {listing.clothing_subcategory && (
                    <>
                      <dt className="text-slate-500">Type</dt>
                      <dd className="font-bold text-slate-900" data-testid="listing-clothing-subcategory">{clothingSubcategoryLabel(listing.clothing_subcategory)}</dd>
                    </>
                  )}
                  {listing.clothing_size && (
                    <>
                      <dt className="text-slate-500">Size</dt>
                      <dd className="font-bold text-slate-900" data-testid="listing-clothing-size">{listing.clothing_size}</dd>
                    </>
                  )}
                  {listing.clothing_gender && (
                    <>
                      <dt className="text-slate-500">Gender</dt>
                      <dd className="font-bold text-slate-900" data-testid="listing-clothing-gender">{clothingGenderLabel(listing.clothing_gender)}</dd>
                    </>
                  )}
                  {listing.clothing_brand && (
                    <>
                      <dt className="text-slate-500">Brand</dt>
                      <dd className="font-bold text-slate-900" data-testid="listing-clothing-brand">{listing.clothing_brand}</dd>
                    </>
                  )}
                </dl>
              </div>
            )}

            {listing.kid_alias && (
              <div className="card-base p-4 mt-6 flex items-center gap-3" data-testid="listing-seller-card">
                <div className="w-10 h-10 rounded-full bg-amber-400 text-amber-950 flex items-center justify-center font-bold">{listing.kid_alias[0]}</div>
                <div className="flex-1">
                  <div className="text-sm text-slate-500">Listed by</div>
                  <div className="font-bold text-slate-900">
                    {listing.kid_alias}
                    {listing.seller_avg_rating != null && (
                      <span className="ml-2 inline-flex items-center text-sm text-amber-600">
                        <Star className="w-3.5 h-3.5 fill-amber-500 mr-0.5" />{listing.seller_avg_rating.toFixed(1)}
                        <span className="text-slate-400 text-xs ml-1">({listing.seller_rating_count})</span>
                      </span>
                    )}
                  </div>
                  {listing.cause_kid_id && (
                    <div className="mt-1.5 inline-flex items-center gap-1.5 text-[12px] font-bold text-rose-700" data-testid="listing-fundraise-line">
                      <HeartHandshake className="w-4 h-4" />
                      Fundraising for {listing.cause_name || "a Cause"} — {listing.cause_split_pct || 100}% of proceeds go to {listing.cause_name || "the cause"}
                    </div>
                  )}
                  {listing.seller_id_verified && (
                    <div
                      className="mt-1 inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200"
                      title="Adult Verified Parent"
                      data-testid="listing-detail-verified-parent"
                    >
                      <ShieldCheck className="w-3 h-3" /> Adult Verified Parent <VerifiedExplainer testId="adult-verified-explainer-seller" />
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Fulfillment selector — 4 options matching mobile spec */}
            <div className="card-base p-4 mt-4" data-testid="listing-fulfillment">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                <div className="text-sm font-bold text-slate-700">How would you like to receive it?</div>
                {listing.package_size && (
                  <span
                    className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 border border-slate-200"
                    data-testid="listing-parcel-size"
                  >
                    <Package className="w-3 h-3" /> {
                      listing.package_size === "small" ? "Small (Royal Mail)" :
                      listing.package_size === "medium" ? "Medium (Royal Mail)" :
                      listing.package_size === "large" ? "Large (Royal Mail)" :
                      listing.package_size === "bulky" ? "Bulky Item" :
                      listing.package_size
                    }
                  </span>
                )}
              </div>
              {/* Iteration 75 — bulky listings waiting on parent selection */}
              {listing.fulfillment_pending_parent && (
                <div
                  className="mb-3 rounded-2xl bg-amber-50 border border-amber-200 px-3 py-2 text-[12px] text-amber-900"
                  data-testid="listing-bulky-pending"
                >
                  <strong>Bulky item</strong> — waiting for the seller&apos;s parent to pick delivery options. Contact will happen through Parent-to-Parent chat once approved.
                </div>
              )}
              <div className="grid grid-cols-2 gap-3" data-testid="fulfillment-options">
                {FULFILLMENT_OPTIONS.map((opt) => {
                  const t = TONE_STYLES[opt.tone] || TONE_STYLES.slate;
                  const selected = fulfillment === opt.value;
                  // Bulky-only options only render for bulky listings.
                  if (opt.bulkyOnly && listing.package_size !== "bulky") return null;
                  // Home delivery is only picked if the seller has shipping enabled
                  // Iteration 128 — Royal Mail fallback: if the seller
                  // has no other buyer-selectable options, unblock
                  // Royal Mail so buyers can always complete checkout.
                  const shippingBlocked = opt.value === "home_delivery"
                    && !listing.shipping_available
                    && !royalMailFallbackActive;
                  // Parent-restricted set: if the parent has decided which
                  // fulfillment options are allowed for this listing, only show those.
                  // Iteration 262 — uses effectiveParentChoices so legacy
                  // mobile values ("collection_only") expand to the local
                  // handover tiles instead of freezing the whole picker.
                  const parentAllowed = !!effectiveParentChoices;
                  // Iteration 128 — Royal Mail is exempt from the
                  // parent-allow list when the fallback is active,
                  // otherwise buyers with legacy listings whose parent
                  // choices exclude home_delivery would still be stuck.
                  const parentBlocked = parentAllowed
                    && !effectiveParentChoices.has(opt.value)
                    && !(opt.value === "home_delivery" && royalMailFallbackActive);
                  const disabled = opt.disabled || shippingBlocked || parentBlocked;
                  const cost = opt.value === "home_delivery"
                    ? formatGBP(HOME_DELIVERY_HANDLING_GBP)
                    : opt.handling === 0
                      ? "£0.00"
                      : formatGBP(opt.handling);
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => !disabled && setFulfillment(opt.value)}
                      disabled={disabled}
                      className={`relative p-3 rounded-2xl border-2 text-left transition-all ${
                        selected && !disabled
                          ? opt.value === "home_delivery"
                            // Iteration 268 — Royal Mail keeps its RED
                            // highlight border when selected (default
                            // choice). Unselected tiles stay standard.
                            ? "border-rose-500 ring-1 ring-rose-500 bg-rose-50"
                            : t.ring + " " + t.bg
                          : disabled
                            ? "border-slate-200 opacity-60 cursor-not-allowed"
                            : "border-slate-200 hover:border-slate-400"
                      }`}
                      data-testid={`fulfill-${opt.value}`}
                    >
                      <div className="flex items-center justify-between">
                        <span className={selected ? "text-brandSlate" : "text-slate-500"}>{FULFILLMENT_ICONS[opt.value]}</span>
                        {opt.comingSoon && (
                          <span
                            className="pill bg-brandSlate/10 text-brandSlate !text-[10px] !py-0.5 !px-2 inline-flex items-center gap-1"
                            data-testid={`${opt.value}-coming-soon`}
                          >
                            <Clock className="w-3 h-3" /> Coming soon
                          </span>
                        )}
                      </div>
                      <div className="font-bold text-slate-900 text-sm mt-1.5">{opt.label}</div>
                      <div className="text-xs text-slate-500 mt-0.5">{opt.subtitle}</div>
                      <div className="text-xs font-semibold text-brandTeal mt-1" data-testid={`${opt.value}-cost`}>
                        {shippingBlocked ? "Seller shipping not enabled" : (
                          opt.value === "home_delivery" && royalMailFallbackActive
                            ? formatGBP(HOME_DELIVERY_HANDLING_GBP)
                            : cost
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Iteration 214 — Section 1: Contact Details (Full Name,
                Guest Email, Guest Mobile). Rendered BEFORE the
                delivery address so buyers give us who they are first.
                Full Name doubles as the Royal Mail recipient label so
                there's no duplicate "recipient name" field lower down. */}
            {needsGuestContact && (
              <div
                className="mt-4 card-base p-4 space-y-3"
                style={{ display: "block", visibility: "visible", opacity: 1, zIndex: 10, position: "relative" }}
                data-testid="guest-contact-panel"
              >
                <div className="text-sm font-bold text-slate-700">
                  Contact details
                </div>
                <p className="text-xs text-slate-500 leading-relaxed">
                  We'll use your name on the parcel and email/text you
                  {fulfillment === "school_gate"
                    ? " to arrange the school-gate collection."
                    : fulfillment === "parent_handoff"
                      ? " to arrange the parent hand-off."
                      : " to keep you posted about your Royal Mail delivery."}
                </p>
                <div>
                  <label htmlFor="guest-buyer-name" className="text-xs font-semibold text-slate-700">
                    Full Name <span className="text-rose-600">*</span>
                  </label>
                  <input
                    id="guest-buyer-name"
                    type="text"
                    autoComplete="name"
                    required
                    value={guestName}
                    onChange={(e) => { setGuestName(e.target.value); setErr(""); }}
                    placeholder="Used for the parcel label and your order contact"
                    className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm focus:border-brandTeal focus:ring-2 focus:ring-brandTeal/30 outline-none"
                    data-testid="guest-buyer-name-input"
                  />
                </div>
                <div>
                  <label htmlFor="guest-buyer-email" className="text-xs font-semibold text-slate-700">
                    Guest Email <span className="text-rose-600">*</span>
                  </label>
                  <input
                    id="guest-buyer-email"
                    type="email"
                    autoComplete="email"
                    required
                    value={guestEmail}
                    onChange={(e) => { setGuestEmail(e.target.value); setErr(""); }}
                    placeholder="Enter your email address"
                    className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm focus:border-brandTeal focus:ring-2 focus:ring-brandTeal/30 outline-none"
                    data-testid="guest-buyer-email-input"
                  />
                </div>
                <div>
                  <label htmlFor="guest-buyer-mobile" className="text-xs font-semibold text-slate-700">
                    Guest Mobile <span className="text-rose-600">*</span>
                  </label>
                  <input
                    id="guest-buyer-mobile"
                    type="tel"
                    autoComplete="tel"
                    required
                    value={guestMobile}
                    onChange={(e) => { setGuestMobile(e.target.value); setErr(""); }}
                    placeholder="Enter your mobile number"
                    className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm focus:border-brandTeal focus:ring-2 focus:ring-brandTeal/30 outline-none"
                    data-testid="guest-buyer-mobile-input"
                  />
                </div>
              </div>
            )}

            {/* Delivery address form — only when Royal Mail Post is chosen.
                Iteration 214 — recipient name is inherited from the
                Contact Details Full Name so buyers never enter it
                twice. This block only captures the address itself. */}
            {isHomeDelivery && (
              <div className="card-base p-4 mt-4" data-testid="delivery-address-form">
                <div className="text-sm font-bold text-slate-700 mb-3">Where should we ship it?</div>

                {/* UK postcode lookup — powered by getAddress.io via the
                    backend proxy. Falls back to the manual form when the
                    provider is unset or the postcode returns 0 rows. */}
                <div className="mb-3" data-testid="address-lookup-block">
                  <label className="block text-xs font-bold text-slate-600 mb-1">Find your address</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={lookupPostcode}
                      onChange={(e) => setLookupPostcode(e.target.value.toUpperCase())}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); runPostcodeLookup(); } }}
                      placeholder="e.g. SW1A 1AA"
                      className="input-base uppercase flex-1"
                      data-testid="address-lookup-postcode-input"
                      aria-label="Postcode for address lookup"
                    />
                    <button
                      type="button"
                      onClick={runPostcodeLookup}
                      disabled={lookupBusy}
                      className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-brandTeal hover:bg-brandTealDark text-white font-bold text-sm transition disabled:opacity-60 flex-shrink-0"
                      data-testid="address-lookup-find-btn"
                    >
                      {lookupBusy
                        ? (<><Loader2 className="w-4 h-4 animate-spin" /> Finding…</>)
                        : (<><Search className="w-4 h-4" /> Find Address</>)}
                    </button>
                  </div>
                  {lookupErr && (
                    <div className="mt-2 text-xs text-rose-700" data-testid="address-lookup-error">{lookupErr}</div>
                  )}
                  {selectedAddressLabel && (
                    <div
                      className="mt-2 rounded-xl border border-brandTeal/25 bg-brandTeal/5 px-3 py-2 text-xs text-brandTealDark inline-flex items-center gap-2"
                      data-testid="address-lookup-selected"
                    >
                      <CheckCircle2 className="w-3.5 h-3.5" /> Found: <span className="font-semibold">{selectedAddressLabel}</span> — now type your house number & street below.
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => setManualEntry((v) => !v)}
                    className="mt-2 inline-flex items-center gap-1 text-xs font-bold text-brandTeal hover:text-brandTealDark underline underline-offset-2"
                    data-testid="address-manual-toggle"
                  >
                    <Edit className="w-3 h-3" /> {manualEntry ? "Hide manual entry" : "Enter address manually"}
                  </button>
                </div>

                {manualEntry && (
                  <div data-testid="delivery-address-manual-fields">
                    <label className="block text-xs font-bold text-slate-600 mb-1">Street address</label>
                    <input
                      type="text"
                      required
                      value={address.line1}
                      onChange={(e) => setAddress({ ...address, line1: e.target.value })}
                      placeholder="123 Example Road"
                      className="input-base mb-3"
                      data-testid="delivery-address-line1"
                    />
                    <label className="block text-xs font-bold text-slate-600 mb-1">Flat / apartment <span className="text-slate-400 font-normal">(optional)</span></label>
                    <input
                      type="text"
                      value={address.line2}
                      onChange={(e) => setAddress({ ...address, line2: e.target.value })}
                      placeholder="Flat 4B"
                      className="input-base mb-3"
                      data-testid="delivery-address-line2"
                    />
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-bold text-slate-600 mb-1">City</label>
                        <input
                          type="text"
                          required
                          value={address.city}
                          onChange={(e) => setAddress({ ...address, city: e.target.value })}
                          placeholder="London"
                          className="input-base"
                          data-testid="delivery-address-city"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-slate-600 mb-1">Postcode</label>
                        <input
                          type="text"
                          required
                          value={address.postcode}
                          onChange={(e) => setAddress({ ...address, postcode: e.target.value.toUpperCase() })}
                          placeholder="E8 1AB"
                          className="input-base uppercase"
                          data-testid="delivery-address-postcode"
                        />
                      </div>
                    </div>
                    {address.postcode && !UK_POSTCODE_RE.test(address.postcode.trim()) && (
                      <div className="text-xs text-rose-600 mt-2" data-testid="delivery-address-postcode-error">
                        That doesn&rsquo;t look like a UK postcode.
                      </div>
                    )}
                  </div>
                )}
                <div className="text-[11px] text-slate-500 mt-3">
                  UK-only shipping. We pass this address securely to the seller&rsquo;s parent via Stripe.
                </div>
              </div>
            )}

            {/* Reserved / pending-parent-approval banner. Iteration 130 —
                copy updated to "No longer available — I want to be
                notified" per user request so the CTA to join the
                waitlist is unambiguous. */}
            {["pending_parent_approval", "reserved"].includes(listing.status) && (
              <div className="mt-4 rounded-2xl border-2 border-amber-200 bg-amber-50 px-4 py-3" data-testid="listing-reserved-badge">
                <div className="flex items-start gap-3">
                  <Lock className="w-4 h-4 text-amber-700 mt-0.5 flex-shrink-0" />
                  <div className="text-sm flex-1 min-w-0">
                    <div className="font-bold text-amber-900" data-testid="listing-reserved-title">
                      No longer available &mdash; I want to be notified
                    </div>
                    <div className="text-xs text-amber-800 mt-0.5 leading-relaxed">
                      Another buyer is waiting on their parent to approve this order. Tap the notify button to be first in line if it&rsquo;s released.
                    </div>
                  </div>
                </div>
                {/* Non-kid viewers get a plain notify button here; kid
                    viewers see the full Piggy Bank waitlist controls
                    below in the checkout panel. */}
                {!isKid && (
                  <button
                    type="button"
                    onClick={() => {
                      setNotifyErr(""); setNotifyOk(false); setNotifyOpen(true);
                    }}
                    className="mt-3 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-2xl bg-amber-600 hover:bg-amber-700 text-white font-bold text-sm shadow-sm transition"
                    data-testid="listing-reserved-notify-btn"
                  >
                    <Bell className="w-4 h-4" /> Notify me when available
                  </button>
                )}
              </div>
            )}

            {isKid ? (
              /* --- Kid Piggy Bank checkout (Phase 2 UI stub) -------------- */
              (() => {
                const priceGBP = listing.price || 0;
                const feeGBP = Math.round(priceGBP * 0.10 * 100) / 100;
                const isPostKid = fulfillment === "home_delivery";
                const postageGBP = isPostKid ? HOME_DELIVERY_HANDLING_GBP : 0;
                const totalGBP = Math.round((priceGBP + feeGBP + postageGBP) * 100) / 100;
                const balancePence = kidWallet?.wallet_pennies ?? 0;
                const balanceGBP = balancePence / 100;
                const totalPence = Math.round(totalGBP * 100);
                const hasEnough = balancePence >= totalPence;
                const short = Math.max(0, totalPence - balancePence);
                const shortGBP = short / 100;
                const isReserved = ["pending_parent_approval", "reserved"].includes(listing.status);
                return (
                  <div className="mt-4 rounded-3xl border border-brandPink/25 bg-gradient-to-br from-brandPink/8 via-white to-brandTeal/10 p-5" data-testid="kid-piggy-bank-checkout">
                    <div className="flex items-start gap-3">
                      <div className="w-11 h-11 rounded-2xl bg-brandPink text-white inline-flex items-center justify-center flex-shrink-0 shadow-sm">
                        <PiggyBank className="w-5 h-5" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-brandPink">Pay from Piggy Bank</div>
                        <div className="font-display text-lg font-semibold text-brandSlate">Your wallet balance</div>
                        <div className="text-2xl font-display font-bold text-emerald-800 mt-0.5" data-testid="kid-wallet-balance-checkout">
                          {kidWallet ? formatGBP(balanceGBP) : <span className="inline-flex items-center gap-1 text-sm text-slate-400"><Loader2 className="w-3.5 h-3.5 animate-spin" /> loading&hellip;</span>}
                        </div>
                      </div>
                    </div>

                    <dl className="mt-4 text-sm space-y-1.5" data-testid="kid-fee-breakdown">
                      <div className="flex items-center justify-between">
                        <dt className="text-slate-600">Item price</dt>
                        <dd className="font-semibold text-slate-900" data-testid="kid-fee-price">{formatGBP(priceGBP)}</dd>
                      </div>
                      {isPostKid && (
                        <div className="flex items-center justify-between" data-testid="kid-fee-postage-row">
                          <dt className="text-slate-600">Postage (Royal Mail)</dt>
                          <dd className="font-semibold text-slate-900" data-testid="kid-fee-postage">{formatGBP(postageGBP)}</dd>
                        </div>
                      )}
                      <div className="flex items-center justify-between">
                        <dt className="text-slate-600">Family Safety &amp; Buyer Protection Fee (10%)</dt>
                        <dd className="font-semibold text-slate-900" data-testid="kid-fee-platform">{formatGBP(feeGBP)}</dd>
                      </div>
                      {listing.cause_kid_id && (
                        <div className="flex items-center justify-between" data-testid="kid-fee-fundraising-row">
                          <dt className="text-rose-700 font-semibold">Fundraising Contribution to {listing.cause_name || "the cause"}</dt>
                          <dd className="font-semibold text-rose-700" data-testid="kid-fee-fundraising">
                            {formatGBP(causeContributionGBP(listing.price, listing.cause_split_pct))}
                          </dd>
                        </div>
                      )}
                      <div className="border-t border-slate-100 pt-2 mt-2 flex items-center justify-between">
                        <dt className="text-brandSlate font-bold">Total</dt>
                        <dd className="text-xl font-display font-semibold text-brandSlate" data-testid="kid-fee-total">{formatGBP(totalGBP)}</dd>
                      </div>
                    </dl>
                    {listing.cause_kid_id && (
                      <p
                        className="text-[11px] text-rose-800 mt-2 bg-rose-50 border border-rose-200 rounded-xl px-3 py-2 leading-relaxed"
                        data-testid="kid-cause-solicitation-statement"
                      >
                        {solicitationStatement(listing.cause_name)}
                      </p>
                    )}

                    {/* Iteration 261 — Instant Buy state reflector. Total
                        compared = item price + 10% fee (same total the
                        purchase request sends and the parent's rule
                        checks). */}
                    {instantRule && (() => {
                      const purchaseTotalPence = Math.round(priceGBP * 100) + Math.round(priceGBP * 0.10 * 100);
                      const eligible = !!instantRule.enabled
                        && purchaseTotalPence <= (instantRule.threshold_pennies || 0);
                      return eligible ? (
                        <div className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-emerald-100 border border-emerald-300 px-3 py-1.5 text-xs font-bold text-emerald-800" data-testid="kid-instant-buy-eligible">
                          <Zap className="w-3.5 h-3.5" /> Instant Buy available with Piggy Bank
                        </div>
                      ) : (
                        <div className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-amber-50 border border-amber-200 px-3 py-1.5 text-xs font-bold text-amber-800" data-testid="kid-requires-approval">
                          <Hourglass className="w-3.5 h-3.5" /> Requires Parent Approval
                        </div>
                      );
                    })()}

                    {kidPurchaseFlash && (
                      <div className="mt-3 rounded-xl bg-emerald-50 border border-emerald-200 px-3 py-2 text-xs text-emerald-800" data-testid="kid-purchase-flash">
                        <CheckCircle2 className="w-3.5 h-3.5 inline mr-1" /> {kidPurchaseFlash}
                      </div>
                    )}
                    {kidPurchaseErr && (
                      <div className="mt-3 rounded-xl bg-rose-50 border border-rose-200 px-3 py-2 text-xs text-rose-800" data-testid="kid-purchase-error">{kidPurchaseErr}</div>
                    )}

                    {/* Iteration 297 — kid-to-kid Best Offer box. Floor-met
                        offers auto-accept and settle from the Piggy Bank
                        instantly (no parent wait). */}
                    {listing.pay_what_you_can && (
                      <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50/60 p-4" data-testid="kid-offer-box">
                        <div className="text-xs font-bold uppercase tracking-wider text-amber-800 inline-flex items-center gap-1.5">
                          <HandCoins className="w-3.5 h-3.5" /> Make your best offer
                        </div>
                        <p className="text-[11px] text-slate-600 mt-1 leading-relaxed">
                          Offers at or above the seller's secret floor price are accepted instantly — paid from your Piggy Bank, no parent wait!
                        </p>
                        <div className="mt-2 flex items-center gap-2 flex-wrap">
                          <div className="inline-flex items-center rounded-full border border-slate-200 bg-white overflow-hidden focus-within:border-amber-400 focus-within:ring-2 focus-within:ring-amber-300/30">
                            <span className="pl-3 pr-1 text-sm font-bold text-slate-500">£</span>
                            <input
                              type="number"
                              min="0.50"
                              step="0.50"
                              inputMode="decimal"
                              value={offerPence ? (offerPence / 100).toString() : ""}
                              onChange={(e) => setOfferPence(Math.max(0, Math.round(parseFloat(e.target.value || "0") * 100)))}
                              className="w-24 py-2 pr-3 text-sm font-mono font-bold text-slate-900 bg-transparent outline-none"
                              placeholder="0.00"
                              data-testid="kid-offer-input"
                            />
                          </div>
                          <button
                            type="button"
                            onClick={kidOffer}
                            disabled={offerBusy || !(offerPence >= 50) || listing.status !== "live"}
                            className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full bg-amber-400 hover:bg-amber-500 text-white font-bold text-sm shadow-sm transition disabled:opacity-60"
                            data-testid="kid-offer-submit"
                          >
                            {offerBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <HandCoins className="w-4 h-4" />}
                            Make Offer
                          </button>
                        </div>
                        {offerInfo?.type === "below_floor" && (
                          <div className="mt-2 rounded-xl bg-white border border-amber-200 px-3 py-2 text-xs text-amber-800" data-testid="kid-offer-below-floor">
                            That offer is below the seller's minimum — try a higher offer, or{" "}
                            <button
                              type="button"
                              className="font-bold underline"
                              onClick={() => kidPurchase(offerPence)}
                              data-testid="kid-offer-parent-route"
                            >
                              send it to your parent for approval
                            </button>.
                          </div>
                        )}
                        {offerInfo?.type === "insufficient_funds" && (
                          <div className="mt-2 rounded-xl bg-rose-50 border border-rose-200 px-3 py-2 text-xs text-rose-800" data-testid="kid-offer-insufficient">
                            <div>
                              Your offer was accepted at {formatGBP(offerPence / 100)}, but your Piggy Bank is {formatGBP((offerInfo.shortfall_pennies || 0) / 100)} short.
                            </div>
                            <div className="mt-1.5 flex gap-2 flex-wrap">
                              <button
                                type="button"
                                onClick={() => askAllowance(offerPence)}
                                disabled={allowanceRequested}
                                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white border border-brandPink text-brandPink font-bold text-[11px] transition disabled:opacity-60"
                                data-testid="kid-offer-topup-btn"
                              >
                                <Send className="w-3 h-3" /> {allowanceRequested ? "Asked! Parent will see it soon" : "Ask Parent to Top Up"}
                              </button>
                              <span className="text-[11px] text-slate-500 self-center">or a parent can pay by card from their dashboard.</span>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {isReserved ? (
                      /* Reserved listing — no purchase, offer waitlist */
                      waitlist.joined ? (
                        <button
                          type="button"
                          disabled
                          className="w-full mt-4 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-2xl bg-emerald-100 text-emerald-800 font-bold text-sm"
                          data-testid="kid-waitlist-joined-btn"
                        >
                          <CheckCircle2 className="w-4 h-4" /> You&rsquo;re on the Waitlist{waitlist.position ? ` · #${waitlist.position}` : ""}
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={joinWaitlist}
                          disabled={waitlistBusy}
                          className="w-full mt-4 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-2xl bg-brandTeal hover:bg-brandTealDark text-white font-bold text-sm shadow-sm transition disabled:opacity-60"
                          data-testid="kid-waitlist-join-btn"
                        >
                          {waitlistBusy
                            ? (<><Loader2 className="w-4 h-4 animate-spin" /> Joining&hellip;</>)
                            : (<><Bell className="w-4 h-4" /> Notify Me / Join Waitlist</>)}
                        </button>
                      )
                    ) : hasEnough ? (
                      <button
                        type="button"
                        onClick={kidPurchase}
                        disabled={kidPurchasing || listing.status !== "live"}
                        className="w-full mt-4 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-2xl bg-brandPink hover:bg-brandPinkDark text-white font-bold text-sm shadow-md transition disabled:opacity-60"
                        data-testid="kid-piggy-bank-pay-btn"
                      >
                        {kidPurchasing
                          ? (<><Loader2 className="w-4 h-4 animate-spin" /> Asking your parent&hellip;</>)
                          : (<><PiggyBank className="w-4 h-4" /> Pay {formatGBP(totalGBP)} from Piggy Bank</>)}
                      </button>
                    ) : (
                      <>
                        <button
                          type="button"
                          disabled
                          className="w-full mt-4 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-2xl bg-slate-100 text-slate-500 font-bold text-sm cursor-not-allowed"
                          data-testid="kid-piggy-bank-short-btn"
                          aria-disabled="true"
                        >
                          <PiggyBank className="w-4 h-4" /> Need {formatGBP(shortGBP)} More in Piggy Bank
                        </button>
                        <button
                          type="button"
                          onClick={askAllowance}
                          disabled={allowanceRequested}
                          className="w-full mt-2 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-2xl bg-white border-2 border-brandPink text-brandPink hover:bg-brandPink/5 font-bold text-sm transition disabled:opacity-60"
                          data-testid="kid-ask-allowance-btn"
                        >
                          {allowanceRequested
                            ? (<><CheckCircle2 className="w-4 h-4" /> Asked! Parent will see it soon</>)
                            : (<><Send className="w-4 h-4" /> Ask Parent for Allowance</>)}
                        </button>
                      </>
                    )}
                    <div className="text-[11px] text-slate-500 mt-3 text-center leading-relaxed">
                      Your parent has 24 hours to approve. If they don&rsquo;t, your Piggy Bank stays untouched.
                    </div>
                  </div>
                );
              })()
            ) : (
              /* --- Parent Stripe checkout (existing) --------------------- */
              <>
                {/* Fee breakdown — mobile spec parity */}
                {listing.price > 0 && (() => {
                  const isPost = fulfillment === "home_delivery";
                  const f = computeFees(listing.price, {
                    external: true,
                    postage_gbp: isPost ? HOME_DELIVERY_HANDLING_GBP : 0,
                  });
                  return (
                    <div className="card-base p-4 mt-4 relative" data-testid="listing-fee-breakdown">
                      <div className="text-sm font-bold text-slate-700 mb-2">You&rsquo;ll pay</div>
                      <dl className="text-sm space-y-1.5">
                        <div className="flex items-center justify-between">
                          <dt className="text-slate-600">Item price</dt>
                          <dd className="font-semibold text-slate-900" data-testid="fee-item-price">{formatGBP(f.gross)}</dd>
                        </div>
                        {isPost && (
                          <div className="flex items-center justify-between" data-testid="fee-postage-row">
                            <dt className="text-slate-600">Postage (Royal Mail)</dt>
                            <dd className="font-semibold text-slate-900" data-testid="fee-postage">{formatGBP(f.postage)}</dd>
                          </div>
                        )}
                        {listing.cause_kid_id && (
                          <div className="flex items-center justify-between" data-testid="fee-fundraising-row">
                            <dt className="text-rose-700 font-semibold">Fundraising Contribution to {listing.cause_name || "the cause"}</dt>
                            <dd className="font-semibold text-rose-700" data-testid="fee-fundraising-contribution">
                              {formatGBP(causeContributionGBP(listing.price, listing.cause_split_pct))}
                            </dd>
                          </div>
                        )}
                        <div className="flex items-center justify-between">
                          <dt className="text-slate-600 inline-flex items-center gap-1.5">
                            {listing.cause_kid_id
                              ? "Platform Tech & Admin Fee (10%) — Digitally Prepared Ltd"
                              : <>Family Safety &amp; Buyer Protection Fee (10%)</>}
                            <InfoTooltip label={FEE_TOOLTIP_COPY} />
                          </dt>
                          <dd className="font-semibold text-slate-900" data-testid="fee-safety-fee">{formatGBP(f.safety_fee)}</dd>
                        </div>
                        <div className="flex items-center justify-between">
                          <dt className="text-slate-600">
                            {listing.cause_kid_id
                              ? "Payment Processing Fee — Stripe"
                              : "Stripe Payment Processing Fee (1.5% + 20p)"}
                          </dt>
                          <dd className="font-semibold text-slate-900" data-testid="fee-stripe-processing">{formatGBP(f.stripe_fee)}</dd>
                        </div>
                        <div className="border-t border-slate-100 pt-2 mt-2 flex items-center justify-between">
                          <dt className="text-brandSlate font-bold">Total</dt>
                          <dd className="text-xl font-display font-semibold text-brandSlate" data-testid="fee-total">{formatGBP(f.buyer_total)}</dd>
                        </div>
                      </dl>
                      <p className="text-[11px] text-slate-500 mt-2" data-testid="fee-note">
                        10% Family Safety &amp; Buyer Protection Fee applies to all transactions.
                        External card checkout also passes through the Stripe processing fee.
                      </p>
                      {listing.cause_kid_id && (
                        <p
                          className="text-[11px] text-rose-800 mt-3 bg-rose-50 border border-rose-200 rounded-xl px-3 py-2 leading-relaxed"
                          data-testid="cause-solicitation-statement"
                        >
                          {solicitationStatement(listing.cause_name)}
                        </p>
                      )}
                      {isPost && (
                        <p
                          className="text-[11px] text-slate-600 mt-3 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2"
                          data-testid="postage-notice"
                        >
                          <strong>Postage {formatGBP(HOME_DELIVERY_HANDLING_GBP)} included.</strong> Seller
                          posts the item to your UK address via Royal Mail.
                        </p>
                      )}
                    </div>
                  );
                })()}

                {err && <div className="mt-4 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-xl p-3" data-testid="checkout-error">{err}</div>}

                {/* Iteration 214 — Contact Details + Delivery Address
                    sections were moved higher up in the JSX (rendered
                    BEFORE the fees block) so buyers fill in their
                    details before they see the total. Both sections
                    now live below the fulfillment picker. */}

                <button onClick={buy} disabled={buying || listing.status !== "live" || !addressValid || !guestContactValid}
                  className="btn-primary w-full mt-6 disabled:opacity-60" data-testid="buy-now-btn">
                  {buying ? (<><Loader2 className="w-4 h-4 animate-spin" /> Redirecting&hellip;</>) :
                    listing.status !== "live" ? "No longer available" :
                    !addressValid ? "Enter a UK delivery address" :
                    !guestContactValid ? "Add name, email & mobile to continue" :
                    (<><Sparkles className="w-4 h-4" /> Buy with Stripe &mdash; {formatGBP(computeFees(listing.price, { external: true, postage_gbp: fulfillment === "home_delivery" ? HOME_DELIVERY_HANDLING_GBP : 0 }).buyer_total)}</>)}
                </button>
                <div className="text-xs text-slate-500 mt-3 text-center">Secure checkout &middot; No charge until confirmed on Stripe.</div>
              </>
            )}

            {/* Prompts */}
            {prompts.length > 0 && (
              <div className="mt-6">
                <button type="button" onClick={() => setShowPrompts((v) => !v)}
                  className="text-sm font-bold text-blue-600 inline-flex items-center gap-1 hover:underline" data-testid="prompts-toggle">
                  <MessageCircle className="w-4 h-4" /> {showPrompts ? "Hide" : "Ask the seller a quick question"}
                </button>
                {showPrompts && (
                  <div className="card-base p-4 mt-3" data-testid="prompts-panel">
                    <p className="text-xs text-slate-500 mb-3">Pick a quick message &mdash; it goes to the seller&rsquo;s parent for safety.</p>
                    <div className="grid sm:grid-cols-2 gap-2">
                      {prompts.map((p) => (
                        <button key={p.key} onClick={() => sendPrompt(p.key)} disabled={sendingPrompt === p.key}
                          className="text-left p-3 border border-slate-200 rounded-xl hover:bg-slate-50 transition-all"
                          data-testid={`prompt-${p.key}`}>
                          <div className="text-xs text-slate-500 font-bold uppercase tracking-wider">{p.title || p.key}</div>
                          <div className="text-sm text-slate-800 mt-1">{p.template || p.text}</div>
                        </button>
                      ))}
                    </div>
                    {/* Iteration 118 — free-text composer with contact-info
                        mask preview. The backend also masks on submit so
                        the client-side warning is purely UX; nothing
                        sensitive can leak even if the buyer bypasses it. */}
                    <div className="mt-4 pt-4 border-t border-slate-200" data-testid="prompts-freetext-wrap">
                      <div className="text-xs text-slate-500 mb-2">Or write your own question:</div>
                      {(listing?.cause_kid_id || listing?.seller_account_type === "organization") && (
                        <div className="mb-2 rounded-2xl bg-rose-50 border border-rose-200 px-3 py-2 text-xs text-rose-900 flex items-start gap-2" data-testid="listing-fundraise-chat-note">
                          <HeartHandshake className="w-4 h-4 text-rose-700 flex-shrink-0 mt-0.5" />
                          <div>This is a fundraising item — all payments must complete in-app so the cause receives its share.</div>
                        </div>
                      )}
                      <textarea
                        value={freeText}
                        onChange={(e) => onFreeTextChange(e.target.value)}
                        className="w-full rounded-2xl border border-slate-300 focus:border-brandTeal focus:ring-2 focus:ring-brandTeal/20 outline-none bg-white px-3 py-2 text-sm text-slate-900 min-h-[70px]"
                        placeholder="e.g. Is this still available? Any postage discount for two items?"
                        maxLength={800}
                        data-testid="prompts-freetext-input"
                      />
                      {maskWarning && (
                        <div className="mt-2 rounded-2xl bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900 flex items-start gap-2" data-testid="prompts-mask-warning">
                          <Shield className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" />
                          <div>
                            <div className="font-semibold">For safety, contact details and exact addresses are hidden until payment is complete.</div>
                            <div className="mt-1 text-[11px] text-amber-800">Detected: {maskWarning}. These are auto-hidden when the message is sent.</div>
                          </div>
                        </div>
                      )}
                      {cashWarn && (
                        <div className="mt-2 rounded-2xl bg-rose-50 border border-rose-300 px-3 py-2 text-xs text-rose-900 flex items-start gap-2" data-testid="listing-cash-warning">
                          <Shield className="w-4 h-4 text-rose-700 flex-shrink-0 mt-0.5" />
                          <div>
                            <div className="font-semibold">Fundraising payments must complete in-app.</div>
                            <div className="mt-1 text-[11px] text-rose-800">It looks like you're arranging cash or an off-platform payment. Cause items must be paid through MyTableTop so the donation reaches the cause. Tap Send again to confirm you understand.</div>
                          </div>
                        </div>
                      )}
                      <div className="flex items-center justify-between mt-2">
                        <div className="text-[11px] text-slate-500">{freeText.length}/800</div>
                        <button
                          type="button"
                          onClick={sendFreeTextPrompt}
                          disabled={freeText.trim().length < 3 || sendingPrompt === "__free__"}
                          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-brandTeal hover:bg-brandTeal/90 disabled:bg-slate-300 disabled:cursor-not-allowed text-white text-sm font-bold transition"
                          data-testid="prompts-freetext-send"
                        >
                          {sendingPrompt === "__free__" ? (
                            <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Sending…</>
                          ) : (
                            <><Send className="w-3.5 h-3.5" /> Send</>
                          )}
                        </button>
                      </div>
                    </div>
                    {promptToast && <div className="mt-3 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-xl p-2" data-testid="prompts-toast">{promptToast}</div>}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </section>
      <Footer />
      {/* Iteration 147 — ParentVerificationModal removed from public
          listing / guest checkout per directive. Verification stays
          active for initial parent account signup (Auth flow) only. */}
      {editingOpen && (
        <EditListingModal
          listing={listing}
          onClose={() => setEditingOpen(false)}
          onSaved={(updated) => {
            setEditingOpen(false);
            if (updated && updated.id) setListing((prev) => ({ ...prev, ...updated }));
          }}
        />
      )}
      {/* Iteration 131 — Notify Waitlist modal. Kid session auto-joins
          via the existing upstream waitlist; guest / parent submits an
          email to the local shim so we can ping them on relist. */}
      {notifyOpen && (
        <div
          className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          data-testid="notify-modal"
          onClick={(e) => { if (e.target === e.currentTarget) setNotifyOpen(false); }}
        >
          <div className="bg-white rounded-3xl max-w-md w-full p-6 shadow-2xl">
            <div className="flex items-start gap-3 mb-4">
              <div className="inline-flex items-center justify-center w-11 h-11 rounded-2xl bg-amber-100 text-amber-700">
                <Bell className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <h3 className="font-display text-xl font-semibold text-slate-900">
                  Notify me when available
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  {isKid
                    ? "We'll drop an alert into your Kid Zone the moment it's back in stock."
                    : "We'll email you as soon as this listing goes live again."}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setNotifyOpen(false)}
                className="text-slate-400 hover:text-slate-700"
                data-testid="notify-modal-close"
                aria-label="Close notify modal"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            {notifyOk ? (
              <div
                className="rounded-2xl bg-emerald-50 border border-emerald-200 p-4 text-sm text-emerald-900"
                data-testid="notify-modal-success"
              >
                <div className="font-bold flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4" /> You're on the list!
                </div>
                <p className="mt-1 text-xs">We'll ping you the moment this listing is available again.</p>
                <button
                  type="button"
                  onClick={() => setNotifyOpen(false)}
                  className="mt-3 w-full px-4 py-2 rounded-2xl bg-slate-900 text-white text-sm font-bold hover:bg-slate-800"
                  data-testid="notify-modal-done"
                >
                  Done
                </button>
              </div>
            ) : (
              <>
                {!isKid && (
                  <label className="block mb-3">
                    <span className="text-xs font-bold text-slate-600 uppercase tracking-wider">Your email</span>
                    <input
                      type="email"
                      value={notifyEmail}
                      onChange={(e) => setNotifyEmail(e.target.value)}
                      className="mt-1 w-full rounded-2xl border border-slate-200 px-3 py-2.5 focus:border-brandTeal outline-none bg-white text-sm"
                      placeholder="you@example.com"
                      data-testid="notify-modal-email"
                      autoFocus
                    />
                  </label>
                )}
                {notifyErr && (
                  <div
                    className="mb-3 rounded-2xl bg-rose-50 border border-rose-200 px-3 py-2 text-sm text-rose-800"
                    data-testid="notify-modal-error"
                  >
                    {notifyErr}
                  </div>
                )}
                <button
                  type="button"
                  onClick={async () => {
                    setNotifyErr("");
                    setNotifyBusy(true);
                    try {
                      if (isKid) {
                        await api.post(`/listings/${id}/waitlist/join`);
                      } else {
                        const emailTrimmed = (notifyEmail || "").trim();
                        if (!emailTrimmed || !emailTrimmed.includes("@")) {
                          setNotifyErr("Please enter a valid email address.");
                          setNotifyBusy(false);
                          return;
                        }
                        await api.post(`/listings/${id}/waitlist/guest-join`, { email: emailTrimmed });
                      }
                      setNotifyOk(true);
                    } catch (e) {
                      setNotifyErr("Couldn't sign you up — please try again in a moment.");
                    } finally {
                      setNotifyBusy(false);
                    }
                  }}
                  disabled={notifyBusy}
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-2xl bg-amber-600 hover:bg-amber-700 text-white font-bold text-sm shadow-sm transition disabled:opacity-60"
                  data-testid="notify-modal-submit"
                >
                  {notifyBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bell className="w-4 h-4" />}
                  {isKid ? "Add to my Kid Zone alerts" : "Notify me by email"}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
